import { Api, Model, ModelRow, ModelSearchResult, StringUtil } from "@mcleod/core";
import { ChangeEvent, Snackbar } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutOrderPayablesProcessPrintChecks } from "./autogen/AutogenLayoutOrderPayablesProcessPrintChecks";
import { BackOfficeReportPreview } from "./BackOfficeReportPreview";
import { OrderPayablesProcessStepperProps } from "./OrderPayablesProcessStepper";

export class OrderPayablesProcessPrintChecks extends AutogenLayoutOrderPayablesProcessPrintChecks {
    private payeeIds: string[];
    private physicalCheckCount: number = 0;
    private otherPaymentCount: number = 0;
    private _reportPreviewChecks: BackOfficeReportPreview;
    private _printedCheckRows = [] as ModelRow[];
    public markStepCompleted: (completed?: boolean) => void;
    private checksReportPropertyRecord: ModelRow;

    override async onLoad() {
        this.buttonViewPaymentRecords.addClickListener(async () => {
            this.buttonViewPaymentRecords.busy = true;
            this.displayPrintedChecks(true);
            this.buttonViewPaymentRecords.busy = false;
        });
        this.checksReportPropertyRecord = await Model.searchSingleRecord("lme/general/report-property", {
            id: "Settlement Checks",
            is_active: "Y"
        });
    }

    private configurePrintChecksToImaging() {
        this.switchPrintChecksToImaging.visible = false;
        if (this.checksReportPropertyRecord != null && this.checksReportPropertyRecord.get("print_action") != "N" && this.buttonGeneratePayments.visible) {
            this.switchPrintChecksToImaging.visible = true;
            this.switchPrintChecksToImaging.checked = true;
            if (this.checksReportPropertyRecord.get("print_action") === "D" || this.checksReportPropertyRecord.get("print_action") === "F") {
                this.switchPrintChecksToImaging.enabled = false;
            }
        }
    }

    get reportPreviewChecks(): BackOfficeReportPreview {
        this.initializeReportPreviewChecks();
        return this._reportPreviewChecks;
    }

    private set reportPreviewChecks(value: BackOfficeReportPreview) {
        this._reportPreviewChecks = value;
    }

    private initializeReportPreviewChecks() {
        if (this._reportPreviewChecks == null) {
            this.reportPreviewChecks = new BackOfficeReportPreview("Print Results", false);
            this.add(this.reportPreviewChecks);
        }
    }

    textboxCheckNumberOnChange(event: ChangeEvent) {
        if (event.newValue.length > 8) {
            this.textboxCheckNumber.text = event.oldValue;
        }

        if (isNaN(Number(event.newValue))) {
            this.textboxCheckNumber.text = event.oldValue;
        } else {
            this.buttonGeneratePayments.enabled = !StringUtil.isEmptyString(this.textboxCheckNumber.text);
        }
    }

    textboxCheckNumberOnBlur() {
        if (!StringUtil.isEmptyString(this.textboxCheckNumber.text)) {
            this.textboxCheckNumber.text = StringUtil.leftPad(this.textboxCheckNumber.text, '0', 8);
        }
    }

    public setupVoidButton(props: Partial<OrderPayablesProcessStepperProps>) {
        this.layoutVoidChecks.buttonVoid.addClickListener(async () => {
            this.layoutVoidChecks.buttonVoid.busy = true;
            const checkIds = [];
            const checkPayeeIds = new Set();
            const selectedRows = this.layoutVoidChecks.tableVoidChecks.filteredRows.filter(row => row.selected);
            selectedRows.forEach(row => checkIds.push(row.data.get("id")));
            selectedRows.forEach(row => checkPayeeIds.add(row.data.get("payee_id")));

            try {
                const endpointPath = props.isOffice ? "lme/backoffice/officepayroll/void-checks" : "lme/settlement/order-payables-void-checks";
                const response = await Api.post(endpointPath,
                    {
                        payee_ids: props.payeeIds,
                        payee_types: props.payeeTypes,
                        settlement_ids: props.settlementIds,
                        deduction_ids: props.deductionIds,
                        start_date: props.startDate,
                        end_date: props.endDate,
                        check_date: props.checkDate,
                        cutoff_date: props.cutoffDate,
                        recurring_date: props.recurringDate,
                        check_ids: checkIds.join(","),
                        check_payee_ids: Array.from(checkPayeeIds).join(","),
                        is_office: props.isOffice
                    }
                );
                this.reportPreviewChecks.clearData();
                this.labelInfoPrimary.text = "Generate Payments";
                await this.loadPrintedChecks(props);
                Snackbar.showSnackbar("Checks were successfully voided (" + response?.data[0]?.voided_check_count + ")");
            } catch (e) {
                await CommonDialogs.showError(e, "Error voiding checks");
            }
            this.layoutVoidChecks.buttonVoid.busy = false;
        });
    }

    public setupCancelVoidButton() {
        this.layoutVoidChecks.buttonCancel.addClickListener(() => {
            this.displayPrintedChecks(false);
        });
    }

    public async getNextCheckNumber(props: Partial<OrderPayablesProcessStepperProps>) {
        // Make server side call - is this the correct/best place to call this?
        Api.post("lme/settlement/get-next-check-number",
            {
                payee_types: props.payeeTypes,
                is_office: props.isOffice
            })
        .then(response => {
            if (response?.data[0]?.next_check_number != undefined) {
                this.textboxCheckNumber.text = response?.data[0]?.next_check_number;
            }
        });
    }

    public async generateChecks(props: Partial<OrderPayablesProcessStepperProps>): Promise<any> {
        this.buttonGeneratePayments.busy = true;
        this.reportPreviewChecks.beginSearch();
        try {
            const endpointPath = props.isOffice ? "lme/backoffice/officepayroll/office-payroll-print-checks" : "lme/settlement/order-payables-print-checks";
            const response = await Api.post(endpointPath,
                {
                    payee_ids: props.payeeIds,
                    payee_types: props.payeeTypes,
                    settlement_ids: props.settlementIds,
                    deduction_ids: props.deductionIds,
                    start_date: props.startDate,
                    end_date: props.endDate,
                    check_date: props.checkDate,
                    cutoff_date: props.cutoffDate,
                    check_number: props.checkNumber,
                    currency_code: props.profileRow.get("currency_type"),
                    bonus_code: props.profileRow.get("bonus_code"),
                    override_frequency: props.profileRow.get("override_frequency"),
                    bonus_tax_method: props.profileRow.get("bonus_tax_method"),
                    send_to_imaging: this.switchPrintChecksToImaging.checked,
                    is_office: props.isOffice
                });
            const checks = [...response?.data[0]?.check_data];
            this.physicalCheckCount = response?.data[0]?.physical_check_count;
            this.otherPaymentCount = response?.data[0]?.other_payment_count;

            if (checks && checks.length != 0) {
                this.reportPreviewChecks.endSearch(checks);
                await this.getNextCheckNumber(props);
                if (this.switchPrintChecksToImaging.checked) {
                    Snackbar.showSuccessSnackbar("Checks were successfully generated and sent to Imaging");
                    this.switchPrintChecksToImaging.checked = false;
                    this.switchPrintChecksToImaging.enabled = true;
                }
            }
            else {
                this.reportPreviewChecks.endSearch([]);
            }

            props.checkNumberCount = checks.length - 1;
             //updating records because their status will be different after checks are generated
            this.updateSelectedRows(props);
            this.loadPrintedChecks(props, false);
        } catch (e) {
            await CommonDialogs.showError(e, "Error generating checks");
        }
        this.buttonGeneratePayments.busy = false;
    }

    public set printedCheckRows(rows: ModelRow[]) {
        if (rows == null) {
            this._printedCheckRows = [];
            return;
        }
        this._printedCheckRows = rows;
    }

    public get printedCheckRows() {
        return this._printedCheckRows;
    }

    public async loadPrintedChecks(props: Partial<OrderPayablesProcessStepperProps>, display: boolean = true) {
        const response: ModelSearchResult = await this.layoutVoidChecks.sourceDrsCheck.search({
            payee_ids: props.payeeIds,
            bonus_code: props.profileRow.get("bonus_code")
        });
        this.payeeIds = props.payeeIds.split(",");
        this.printedCheckRows = response?.modelRows;
        this.displayPrintedChecks(display);
    }

    configureUnpostedPaymentsDisplay() {
        this.buttonViewPaymentRecords.visible = false;
        if (this.printedCheckRows.length == this.payeeIds?.length) {
            this.buttonGeneratePayments.visible = false;
            this.textboxCheckNumber.visible = false;
            this.labelInfoPrimary.text = "Payments have been generated";
            this.labelInfoSecondary.visible = true;
            this.labelInfoSecondary.text = "Select unposted records below to void before generating new payments.";
        } else if (this.printedCheckRows.length > 0) {
            this.buttonGeneratePayments.visible = true;
            this.textboxCheckNumber.visible = true;
            this.labelInfoPrimary.text = `Payments have been generated for ${this.printedCheckRows.length} of ${this.payeeIds.length} selected payees`;
            this.labelInfoSecondary.visible = true;
            this.labelInfoSecondary.text = "Generate payments for remaining payees or select to void unposted payment below.";
        } else {
            this.buttonGeneratePayments.visible = true;
            this.textboxCheckNumber.visible = true;
            this.labelInfoSecondary.visible = false;
            this.layoutVoidChecks.visible = false;
        }
        this.configurePrintChecksToImaging();
    }

    public getPrintedChecks(): ModelRow<any> [] {
        return this.layoutVoidChecks.mainDataSource.data;
    }

    public displayPrintedChecks(show: boolean) {
        this.layoutVoidChecks.visible = show;
        this.reportPreviewChecks.visible = !show && !this.reportPreviewChecks.isEmpty();
        if (this.layoutVoidChecks.visible) {
            this.configureUnpostedPaymentsDisplay();
            this.layoutVoidChecks.buttonCancel.visible = !this.reportPreviewChecks.isEmpty();
        } else {
            this.configureReportDisplay();
        }
    }

    configureReportDisplay() {
        this.buttonGeneratePayments.visible = false;
        this.textboxCheckNumber.visible = false;
        this.labelInfoSecondary.visible = true;
        this.buttonViewPaymentRecords.visible = true;
        this.labelInfoPrimary.text = `Checks ready to print (${this.physicalCheckCount})`;
        this.labelInfoSecondary.text = `Additional payment records created (${this.otherPaymentCount})`;
        this.configurePrintChecksToImaging();
    }

    async updateSelectedRows(props: Partial<OrderPayablesProcessStepperProps>) {
        const settlmentIds = props.selectedPayables.filter(row => row.get("record_type") == "S").map(row => row.get("record_id"));
        const adjustmentIds = props.selectedPayables.filter(row => row.get("record_type") == "P").map(row => row.get("record_id"));

        const settlements = (await Model.search("lme/settlement/settlement",{ ids: settlmentIds.join(",") })).modelRows;
        const adjustment = (await Model.search("lme/settlement/drs-pending-deduct",{ ids: adjustmentIds.join(",") })).modelRows;

        this.layoutVoidChecks.idString = "id";
        this.layoutVoidChecks.selectedRows = [...settlements, ...adjustment];
    }
}
